import React, { useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import {
  Button,
  MuiTabs,
  Stack,
  Tab as MuiTab,
  Typography,
} from '@cherre-frontend/ui';
import { CustomPropertyAttribute } from '../../types/CustomPropertyAttribute';
import { CurrentCustomAttributesTab } from './CurrentCustomAttributesTab';
import { ArchivedCustomAttributesTab } from './ArchivedCustomAttributesTab';

const modalHeaderMessages = [
  'Define the structure of your property table by adding, editing, or removing columns (attributes). For each attribute, provide a unique name and select the appropriate data type to ensure data consistency. ',
  'When defining a new property table attribute, you must specify if the attribute is intended for the source entity ID or target entity ID.',
  'By default, the Cherre standard attributes (Target ID, Source ID, Mapping, Provider, Target Name, Address, Fund, and Type) are included.',
];

export enum Tab {
  Current = 'Current',
  Archived = 'Archived',
}

export interface PropertiesCustomAttributesDialogProps {
  onCancel: () => void;
  currentAttributes: CustomPropertyAttribute[];
  onSave: (attributes: CustomPropertyAttribute[]) => void;
}

export const CustomPropertyAttributesDialog = ({
  onCancel,
  onSave,
  currentAttributes,
}: PropertiesCustomAttributesDialogProps) => {
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.Current);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [attributes, setAttributes] = useState(currentAttributes);

  const { activeAttributes, archivedAttributes } = useMemo(() => {
    const activeAttributes = attributes.filter(({ active }) => active);
    const archivedAttributes = attributes.filter(({ active }) => !active);
    return {
      activeAttributes: orderBy(activeAttributes, 'order'),
      archivedAttributes: orderBy(archivedAttributes, 'order'),
    };
  }, [attributes]);

  const addAttribute = (attribute: CustomPropertyAttribute) => {
    setAttributes((prevState) => {
      return [...prevState, { ...attribute, order: prevState.length }];
    });
  };

  const editAttribute = (
    slugToEdit: string,
    attributeToEdit: CustomPropertyAttribute
  ) => {
    setAttributes((prevState) => {
      return prevState.map((attribute) =>
        attribute.slug === slugToEdit ? attributeToEdit : attribute
      );
    });
  };

  const moveAttribute = (fromSlug: string, toSlug: string) => {
    setAttributes((prevState) => {
      const from = prevState.find(({ slug }) => slug === fromSlug);
      const to = prevState.find(({ slug }) => slug === toSlug);
      if (!from || !to || from === to || from.active !== to.active) {
        return prevState;
      }

      return prevState.map((attribute) => {
        const withOrder = (order: number) => ({ ...attribute, order });
        if (attribute.active !== from.active) {
          return attribute;
        } else if (attribute === from) {
          return withOrder(to.order);
        }

        //         |---------- fromDistance ----------|
        //                       |--- attrDistance ---|
        // [..., from, ..., attribute ............., to, ...............]
        //        -3            -2          -1       0       +1       +2

        const fromDistance = from.order - to.order;
        const attrDistance = attribute.order - to.order;
        const isMovingRight = fromDistance < 0;

        const isAttributeInBetweenFromAndTo =
          (isMovingRight && attrDistance > fromDistance && attrDistance <= 0) ||
          (!isMovingRight && attrDistance < fromDistance && attrDistance >= 0);

        if (isAttributeInBetweenFromAndTo) {
          // When from is moved right, all attributes in between move left and vice versa
          const delta = isMovingRight ? -1 : +1;
          return withOrder(attribute.order + delta);
        }

        return attribute;
      });
    });
  };

  const toggleAttributeActive = (slugToToggle: string) => {
    setAttributes((prevState) => {
      const attributeToToggle = prevState.find(
        ({ slug }) => slug === slugToToggle
      );
      if (!attributeToToggle) {
        return prevState;
      }

      const newActiveValue = !attributeToToggle.active;
      const newOrderValue = attributeToToggle.active
        ? archivedAttributes.length
        : activeAttributes.length;

      return prevState.map((attribute) => {
        if (attribute.slug === attributeToToggle.slug) {
          return { ...attribute, active: newActiveValue, order: newOrderValue };
        }

        const shouldUpdateAttributeOrder =
          attribute.active === attributeToToggle.active &&
          attribute.order > attributeToToggle.order;
        return shouldUpdateAttributeOrder
          ? { ...attribute, order: attribute.order - 1 }
          : attribute;
      });
    });
  };

  const restoreAttribute = (slugToRestore: string) => {
    toggleAttributeActive(slugToRestore);
    if (archivedAttributes.length === 1) {
      setSelectedTab(Tab.Current);
    }
  };

  const saveAttributesWithOrder = () => {
    onSave(attributes);
  };

  return (
    <>
      <Stack gap={1}>
        <Typography variant='h5' fontWeight={700}>
          Custom Property Attributes
        </Typography>

        {modalHeaderMessages.map((message, index) => (
          <Typography key={index} variant='body1'>
            {message}
          </Typography>
        ))}
      </Stack>

      <MuiTabs
        value={selectedTab}
        onChange={(_, newTab: Tab) => setSelectedTab(newTab)}
      >
        <MuiTab value={Tab.Current} label={Tab.Current} />
        <MuiTab
          value={Tab.Archived}
          label={Tab.Archived}
          disabled={archivedAttributes.length === 0}
        />
      </MuiTabs>

      {selectedTab === Tab.Current && (
        <CurrentCustomAttributesTab
          allAttributes={attributes}
          activeAttributes={activeAttributes}
          onAddAttribute={addAttribute}
          onEditAttribute={editAttribute}
          onArchiveAttribute={toggleAttributeActive}
          onMoveAttribute={moveAttribute}
          setIsSaveButtonDisabled={setIsSaveButtonDisabled}
        />
      )}

      {selectedTab === Tab.Archived && (
        <ArchivedCustomAttributesTab
          archivedAttributes={archivedAttributes}
          onRestoreAttribute={restoreAttribute}
          onMoveAttribute={moveAttribute}
        />
      )}

      <Stack
        gap={1}
        direction='row'
        justifyContent='flex-end'
        alignItems='center'
        position='sticky'
        bottom={0}
        paddingBottom='20px'
        sx={{ backgroundColor: 'white' }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={isSaveButtonDisabled}
          onClick={saveAttributesWithOrder}
        >
          Save
        </Button>
      </Stack>
    </>
  );
};
